import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, User } from "lucide-react";
import {
  brand, images, aboutParagraphs, didYouKnow, segments, gridFree,
  owner, sustainability, events,
} from "../mock/mock";
import ContactSection from "../components/ContactSection";

function Hero() {
  return (
    <section className="relative h-screen min-h-[640px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={images.heroEngine} alt="Industrial engine hall" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(22,38,92,0.85) 0%, rgba(22,38,92,0.78) 100%)" }} />
      </div>
      <div className="relative text-center px-5 oe-reveal">
        <div className="flex items-center justify-center gap-2 md:gap-3">
          <span className="oe-display text-white text-5xl md:text-8xl">OCEAN</span>
          <span className="inline-block w-4 h-4 md:w-6 md:h-6 rounded-full bg-oe-red" />
          <span className="oe-display text-white text-5xl md:text-8xl">ENERGY</span>
        </div>
        <div className="mt-3 md:mt-4 border-t-2 border-white inline-block px-6">
          <span className="text-white tracking-[0.5em] md:tracking-[0.7em] text-lg md:text-3xl font-semibold">
            INDUSTRIAL
          </span>
        </div>
        <p className="text-white/90 tracking-[0.35em] text-sm md:text-lg font-medium mt-6">{brand.tagline}</p>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 animate-bounce">
        <ChevronDown size={30} />
      </div>
    </section>
  );
}

function AboutSection() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % didYouKnow.length), 4000);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="bg-white">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-1 hidden lg:flex justify-center">
            <div className="flex flex-col items-center gap-4">
              <span className="[writing-mode:vertical-rl] rotate-180 text-oe-navy font-semibold tracking-widest uppercase text-sm">
                About
              </span>
              <span className="w-px h-16 bg-oe-navy/40" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="space-y-5">
              {aboutParagraphs.map((p, i) => (
                <p key={i} className="text-oe-navy/80 leading-relaxed text-[15px] md:text-base">{p}</p>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 flex items-start">
            <div className="oe-navy text-white rounded-lg p-8 md:p-10 w-full min-h-[280px] flex flex-col justify-between">
              <div>
                <h3 className="text-xl font-semibold mb-5">Did you know...</h3>
                <p key={idx} className="text-white/85 leading-relaxed oe-reveal">{didYouKnow[idx]}</p>
              </div>
              <div className="flex gap-2 mt-8">
                {didYouKnow.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`slide ${i + 1}`}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${i === idx ? "bg-white" : "bg-white/30"}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SegmentBlock({ data, image, reverse }) {
  return (
    <div className="grid md:grid-cols-2 min-h-[520px]">
      <div className={`relative oe-navy flex items-center ${reverse ? "md:order-2" : ""}`}>
        <img src={image} alt={data.title} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative px-8 md:px-16 py-16 max-w-xl">
          <h2 className="oe-display text-white text-4xl md:text-5xl mb-6">{data.title}</h2>
          <p className="text-white/85 leading-relaxed mb-8">{data.body}</p>
          <Link to={data.cta.path} className="group inline-flex items-center gap-3 text-white font-semibold">
            <span className="w-10 h-10 rounded-full bg-oe-red flex items-center justify-center group-hover:scale-110 transition-transform">
              <ArrowRight size={18} />
            </span>
            {data.cta.label}
          </Link>
        </div>
      </div>
      <div className={`${reverse ? "md:order-1" : ""}`}>
        <img src={image} alt={data.title} className="w-full h-full object-cover min-h-[300px]" />
      </div>
    </div>
  );
}

function GridFreeSection() {
  return (
    <section className="bg-secondary">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="oe-heading text-oe-navy text-4xl md:text-5xl mb-2">{gridFree.eyebrow}</h2>
            <h3 className="oe-heading text-oe-navy/70 text-4xl md:text-5xl mb-8">{gridFree.title}</h3>
            <p className="text-oe-navy/80 leading-relaxed mb-10 max-w-lg">{gridFree.body}</p>
            <Link to={gridFree.cta.path} className="group inline-flex items-center gap-4 oe-navy text-white font-semibold px-8 py-4 rounded-md hover:oe-navy-2 transition-colors">
              <span className="w-9 h-9 rounded-full border border-white/50 flex items-center justify-center">
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
              {gridFree.cta.label}
            </Link>
          </div>
          <img src={images.gridFree} alt="Grid-free power" className="rounded-lg w-full h-[420px] object-cover shadow-xl" />
        </div>
      </div>
    </section>
  );
}

function OwnerPreview() {
  return (
    <section className="oe-navy text-white">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-24">
        <p className="text-oe-red font-semibold uppercase tracking-[0.3em] text-xs mb-8">About the Owner</p>
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4">
            <div className="aspect-[4/5] rounded-lg border-2 border-dashed border-white/30 bg-white/5 flex flex-col items-center justify-center text-white/50 text-center p-6">
              {owner.photo ? (
                <img src={owner.photo} alt={owner.name} className="w-full h-full object-cover rounded-lg" />
              ) : (
                <>
                  <User size={56} className="mb-4" />
                  <span className="text-sm">Owner photo to be added here</span>
                </>
              )}
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="oe-heading text-3xl md:text-4xl mb-2">{owner.name}</h2>
            <p className="text-white/60 mb-6">{owner.role}</p>
            <p className="text-white/85 leading-relaxed mb-6 max-w-2xl">{owner.intro}</p>
            <blockquote className="border-l-4 border-oe-red pl-5 italic text-white/90 text-lg mb-8 max-w-xl">
              "{owner.quote}"
            </blockquote>
            <Link to="/about-owner" className="group inline-flex items-center gap-3 text-white font-semibold">
              <span className="w-10 h-10 rounded-full bg-oe-red flex items-center justify-center group-hover:scale-110 transition-transform">
                <ArrowRight size={18} />
              </span>
              Read more
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function SustainabilitySection() {
  return (
    <section className="relative overflow-hidden">
      <img src={images.sustainability} alt="Sustainability" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(244,245,247,0.96)" }} />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 py-20 md:py-24">
        <div className="max-w-2xl">
          <p className="text-oe-red font-semibold uppercase tracking-[0.3em] text-xs mb-4">Sustainability</p>
          <h2 className="oe-heading text-oe-navy text-3xl md:text-4xl mb-6">{sustainability.title}</h2>
          <p className="text-oe-navy leading-relaxed mb-8">{sustainability.body}</p>
          <Link to={sustainability.cta.path} className="inline-flex items-center gap-2 text-oe-navy font-semibold border-b-2 border-oe-red pb-1 hover:gap-3 transition-all">
            {sustainability.cta.label} <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-14">
          {sustainability.pillars.map((p) => (
            <div key={p.title} className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all">
              <h4 className="text-oe-navy font-semibold text-lg mb-2">{p.title}</h4>
              <p className="text-muted-foreground text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EventsSection() {
  return (
    <section className="bg-white">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 border-t border-gray-100">
        <h3 className="oe-heading text-oe-navy text-2xl md:text-3xl mb-8">Upcoming Events</h3>
        <div className="flex flex-wrap gap-4">
          {events.map((e, i) => (
            <div key={i} className="flex items-center gap-4 border border-gray-200 rounded-lg px-5 py-4 hover:border-oe-navy transition-colors">
              <div className="oe-navy text-white rounded-md px-3 py-2 text-center min-w-[54px]">
                <span className="block text-sm font-bold leading-tight">{e.date.split(" ")[0]}</span>
                <span className="block text-[10px] uppercase tracking-wide">{e.date.split(" ")[1]}</span>
              </div>
              <span className="text-oe-navy font-medium text-sm">{e.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <AboutSection />
      <SegmentBlock data={segments.onLand} image={images.onLand} />
      <SegmentBlock data={segments.atSea} image={images.atSea} reverse />
      <GridFreeSection />
      <OwnerPreview />
      <SustainabilitySection />
      <EventsSection />
      <ContactSection />
    </main>
  );
}
